import React, { useState, useRef, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../ui/tooltip';
import {
  Send,
  Paperclip,
  Smile,
  X
} from 'lucide-react';
import EmojiPicker from './EmojiPicker';

const MAX_MESSAGE_LENGTH = 4000;

const EnhancedInputArea = ({
  onSendMessage,
  disabled = false,
  isLoading = false,
  placeholder = 'Ask me anything...',
}) => {
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const textAreaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Calculate character count and remaining
  const characterCount = message.length;
  const remainingChars = MAX_MESSAGE_LENGTH - characterCount;
  const isOverLimit = characterCount > MAX_MESSAGE_LENGTH;

  // Check if send button should be enabled
  const canSend = message.trim() && !disabled && !isOverLimit && !isLoading;

  // Handle message input change
  const handleMessageChange = (event) => {
    setMessage(event.target.value);
  };

  // Handle send message
  const handleSend = useCallback(async () => {
    if (!canSend) return;

    const messageToSend = message.trim();
    const filesToSend = [...files];

    setMessage('');
    setFiles([]);

    try {
      await onSendMessage(messageToSend, {
        files: filesToSend,
        metadata: {
          timestamp: new Date().toISOString(),
          hasFiles: filesToSend.length > 0,
        },
      });
    } catch (err) {
      setMessage(messageToSend);
      setFiles(filesToSend);
      console.error('Failed to send message:', err);
    }
  }, [message, files, canSend, onSendMessage]);

  // Handle key press
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  // Handle emoji selection
  const handleEmojiSelect = (emoji) => {
    const textarea = textAreaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newMessage = message.slice(0, start) + emoji + message.slice(end);
      setMessage(newMessage);

      setTimeout(() => {
        textarea.setSelectionRange(start + emoji.length, start + emoji.length);
        textarea.focus();
      }, 0);
    }
    setShowEmojiPicker(false);
  };

  // Remove file
  const handleRemoveFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
  };

  // Handle file input click
  const handleFileInputClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file input change
  const handleFileInputChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const newFiles = [...files, ...selectedFiles];
    setFiles(newFiles);
    event.target.value = '';
  };

  return (
    <div className="relative">

      {/* Emoji Picker */}
      <EmojiPicker
        onEmojiSelect={handleEmojiSelect}
        isOpen={showEmojiPicker}
      />

      {/* File Attachments Display */}
      {files.length > 0 && (
        <div className="mb-2">
          <Card className="p-2 bg-muted">
            <span className="text-xs text-muted-foreground block mb-2">
              附件 ({files.length})
            </span>
            <div className="flex flex-wrap gap-1">
              {files.map((file, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="max-w-[200px] flex items-center gap-1"
                >
                  <span className="truncate">{file.name}</span>
                  <button
                    onClick={() => handleRemoveFile(index)}
                    className="hover:bg-muted rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Main Input Area */}
      <div
        className={`flex items-end gap-2 p-4 bg-card rounded-lg border ${
          isOverLimit ? 'border-destructive' : ''
        }`}
      >
        {/* File Attachment Button */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileInputChange}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.png,.jpg,.jpeg"
        />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleFileInputClick}
                disabled={disabled}
                className="mb-1"
              >
                <Paperclip className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>附加文件</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Text Input */}
        <textarea
          ref={textAreaRef}
          value={message}
          onChange={handleMessageChange}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          className="flex-1 resize-none border-0 bg-transparent text-base leading-6 focus:outline-none focus:ring-0 min-h-[24px] max-h-[150px]"
          style={{
            height: 'auto',
            overflow: 'hidden'
          }}
          onInput={(e) => {
            e.target.style.height = 'auto';
            e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
          }}
        />

        {/* Emoji Button */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                disabled={disabled}
                className="mb-1"
              >
                <Smile className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>添加表情</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Send Button */}
        <Button
          onClick={handleSend}
          disabled={!canSend}
          size="icon"
          className="rounded-full w-10 h-10 mb-1"
        >
          <Send className="h-5 w-5" />
        </Button>
      </div>

      {/* Character Count and Progress */}
      <div className="mt-2 flex items-center justify-between">
        <span className={`text-xs ${isOverLimit ? 'text-destructive' : 'text-muted-foreground'}`}>
          {characterCount} / {MAX_MESSAGE_LENGTH} 个字符
          {isOverLimit && ` (超出限制 ${Math.abs(remainingChars)} 个)`}
        </span>

        {/* Loading Progress */}
        {isLoading && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">发送中...</span>
            <Progress value={undefined} className="w-16 h-1" />
          </div>
        )}

        {/* Keyboard Shortcut Hint */}
        {!isLoading && (
          <span className="text-xs text-muted-foreground">
            Enter 发送，Shift+Enter 换行
          </span>
        )}
      </div>
    </div>
  );
};

export default EnhancedInputArea;
